import { Col, Row, Tabs } from 'antd'
import { Select } from 'common/components/Select/Select'
import { useAppSelector } from 'common/hooks/redux'
import { IOption } from 'common/interfaces/IOption'
import { useProcedureActions } from 'features/Procedure/hooks/useProcedureActions'
import { useEffect, useMemo } from 'react'
import { LabsComparison, LabsComparisonProps } from '../labs/labs-comparison'
import { Notes, NotesProps } from '../notes/Notes'
import { Products, ProductsProps } from '../products'
import { TreatmentsDualComparison } from '../treatments/treatments-dual-comparison'
import { ComparisonContainer } from './components/ComparisonContainer'
import { PhotosComparison } from './components/PhotosComparison'
import styles from './previous.module.scss'
import { getAdjustedEncounterOptions, resolveComparisonEncounterUid } from './utils'

export const CHARTS_PREVIOUS_VISITS_ATTR = 'data-charts-previous-visits'

enum PreviousVisitsTab {
  Treatments = 'treatments',
  Photos = 'photos',
  Notes = 'notes',
  Products = 'products',
  Labs = 'labs',
}

type Props = {
  encounterOptions: IOption[]
  selectedPatientSex?: string | null
  onCanvasReady?: () => void
}

export const PreviousVisits = ({ encounterOptions, selectedPatientSex, onCanvasReady }: Props) => {
  const { handleSetSelectedComparisonEncounterUid, handleSetSelectedEncounterUid } =
    useProcedureActions()

  const selectedEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedEncounterUid,
  )

  const selectedComparisonEncounterUid = useAppSelector(
    (state) => state.procedureReducer.selectedComparisonEncounterUid,
  )

  const selectedPatientId = useAppSelector((state) => state.procedureReducer.selectedPatientId)

  const resolvedComparisonEncounterUid = useMemo(
    () =>
      resolveComparisonEncounterUid({
        primaryEncounterUid: selectedEncounterUid,
        currentComparisonEncounterUid: selectedComparisonEncounterUid,
        encounterOptions,
      }),
    [encounterOptions, selectedComparisonEncounterUid, selectedEncounterUid],
  )

  useEffect(() => {
    if (resolvedComparisonEncounterUid !== selectedComparisonEncounterUid) {
      handleSetSelectedComparisonEncounterUid(resolvedComparisonEncounterUid)
    }
  }, [
    resolvedComparisonEncounterUid,
    selectedComparisonEncounterUid,
    handleSetSelectedComparisonEncounterUid,
  ])

  const mainEncounterOptions = useMemo(
    () => getAdjustedEncounterOptions(encounterOptions, selectedComparisonEncounterUid),
    [encounterOptions, selectedComparisonEncounterUid],
  )

  const comparisonEncounterOptions = useMemo(
    () => getAdjustedEncounterOptions(encounterOptions, selectedEncounterUid),
    [encounterOptions, selectedEncounterUid],
  )

  const onMainEncounterChange = (newEncounterUid: string) => {
    handleSetSelectedEncounterUid(newEncounterUid)
  }

  const onComparisonEncounterChange = (encounterUid: string) => {
    handleSetSelectedComparisonEncounterUid(encounterUid)
  }

  const renderContent = (tab: PreviousVisitsTab) => {
    switch (tab) {
      case PreviousVisitsTab.Treatments:
        return (
          <TreatmentsDualComparison
            selectedPatientId={selectedPatientId}
            selectedPatientSex={selectedPatientSex}
            selectedEncounterUid={selectedEncounterUid}
            selectedComparisonEncounterUid={selectedComparisonEncounterUid}
            className={styles.treatmentsOverride}
            onCanvasReady={onCanvasReady}
          />
        )
      case PreviousVisitsTab.Photos:
        return (
          <PhotosComparison
            selectedPatientId={selectedPatientId}
            selectedEncounterUid={selectedEncounterUid}
            selectedComparisonEncounterUid={selectedComparisonEncounterUid}
            className={styles.photosOverride}
          />
        )
      case PreviousVisitsTab.Notes:
        return (
          <ComparisonContainer<NotesProps>
            component={Notes}
            commonProps={{
              selectedPatientId,
              disableActions: true,
              defaultExpanded: true,
              showTelehealthSoapNotes: true,
              className: styles.notesOverride,
            }}
            leftProps={{ selectedEncounterUid }}
            rightProps={{ selectedEncounterUid: selectedComparisonEncounterUid }}
          />
        )
      case PreviousVisitsTab.Products:
        return (
          <ComparisonContainer<ProductsProps>
            component={Products}
            commonProps={{
              disableActions: true,
              className: styles.productsOverride,
            }}
            leftProps={{ selectedEncounterUid }}
            rightProps={{ selectedEncounterUid: selectedComparisonEncounterUid }}
          />
        )
      case PreviousVisitsTab.Labs:
        return (
          <ComparisonContainer<LabsComparisonProps>
            component={LabsComparison}
            commonProps={{
              selectedPatientId,
              className: styles.labsOverride,
            }}
            leftProps={{ selectedEncounterUid }}
            rightProps={{ selectedEncounterUid: selectedComparisonEncounterUid }}
          />
        )
      default:
        return null
    }
  }

  const items = useMemo(
    () => [
      {
        key: PreviousVisitsTab.Treatments,
        label: 'Treatments',
        children: renderContent(PreviousVisitsTab.Treatments),
        forceRender: true,
      },
      {
        key: PreviousVisitsTab.Photos,
        label: 'Photos',
        children: renderContent(PreviousVisitsTab.Photos),
      },
      {
        key: PreviousVisitsTab.Notes,
        label: 'Notes',
        children: renderContent(PreviousVisitsTab.Notes),
      },
      {
        key: PreviousVisitsTab.Products,
        label: 'Products',
        children: renderContent(PreviousVisitsTab.Products),
      },
      {
        key: PreviousVisitsTab.Labs,
        label: 'Labs',
        children: renderContent(PreviousVisitsTab.Labs),
      },
    ],
    [
      selectedEncounterUid,
      selectedComparisonEncounterUid,
      selectedPatientId,
      selectedPatientSex,
      onCanvasReady,
    ],
  )

  return (
    <div className={styles.container}>
      <Tabs
        defaultActiveKey={PreviousVisitsTab.Treatments}
        items={items}
        destroyOnHidden
        className={styles.navigationTabs}
        renderTabBar={(props, DefaultTabBar) => (
          <Row
            gutter={[12, 12]}
            justify='space-between'
            align='middle'
            className={styles.controlsRow}>
            <Col>
              <Select
                className={styles.encounterSelect}
                value={selectedEncounterUid || undefined}
                onChange={onMainEncounterChange}
                listOptions={mainEncounterOptions}
                placeholder='Select encounter'
              />
            </Col>
            <Col>
              <DefaultTabBar {...props} className={styles.tabBar} />
            </Col>
            <Col>
              <Select
                className={styles.encounterSelect}
                value={selectedComparisonEncounterUid || undefined}
                onChange={onComparisonEncounterChange}
                listOptions={comparisonEncounterOptions}
                placeholder='Select encounter'
              />
            </Col>
          </Row>
        )}
      />
    </div>
  )
}
